import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormPageShell } from '../../components/DataTablePanel';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import { BackButton } from '../../components/BackButton';
import {
  Badge,
  BanIcon,
  Button,
  Callout,
  Card,
  LoadingSpinner,
  PencilIcon,
  useConfirm,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { InvoiceDetail, InvoiceLine } from '../../types/invoice';
import { formatDate, formatMoney } from '../../utils/format';

const lineSortGetters: Record<
  string,
  (line: InvoiceLine) => string | number | boolean | null | undefined
> = {
  type: (l) => l.type,
  amount: (l) => l.amount,
  netAmount: (l) => l.netAmount,
  vatAmount: (l) => l.vatAmount,
  description: (l) => l.description,
};

export function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const confirm = useConfirm();
  const labels = t().invoices;
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('type');
  const displayLines = useMemo(
    () =>
      invoice ? sortItems(invoice.lines, sort, lineSortGetters) : [],
    [invoice, sort],
  );
  const showVatColumns = useMemo(
    () =>
      Boolean(
        invoice?.lines.some(
          (line) => Number(line.vatAmount ?? 0) > 0 || Number(line.vatRate ?? 0) > 0,
        ),
      ),
    [invoice],
  );

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const data = await api.getInvoice(Number(id));
        if (!cancelled) setInvoice(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleDelete() {
    if (!invoice) return;
    const ok = await confirm({
      title: labels.softDelete,
      message: labels.softDeleteConfirm,
      confirmText: labels.softDelete,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteInvoice(invoice.id);
      navigate('/invoices');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
    }
  }

  if (loading) {
    return <LoadingSpinner message={t().common.loading} />;
  }

  if (!invoice) {
    return (
      <Callout variant="danger">
        {error || 'Το τιμολόγιο δεν βρέθηκε'}
      </Callout>
    );
  }

  const statusVariant =
    invoice.status === 'PAID'
      ? 'success'
      : invoice.status === 'OVERDUE'
        ? 'danger'
        : invoice.status === 'PARTIALLY_PAID'
          ? 'warning'
          : 'secondary';

  return (
    <FormPageShell wide contentClassName="space-y-5">
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">
              {invoice.invoiceNo}
            </h2>
            <div className="mt-1 text-sm text-ink-muted">
              {invoice.customer.name} · {formatDate(invoice.invoiceDate)} · Λήξη{' '}
              {formatDate(invoice.dueDate)}
            </div>
            <div className="mt-3">
              <Badge variant={statusVariant}>
                {labels.statuses[invoice.status]}
              </Badge>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <BackButton fallback="/invoices" />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
            >
              <PencilIcon className="h-3.5 w-3.5" />
              {labels.editAction}
            </Button>
            {isAdmin ? (
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => void handleDelete()}
              >
                <BanIcon className="h-3.5 w-3.5" />
                {labels.softDelete}
              </Button>
            ) : null}
          </div>
        </div>
      </Card>

      {error ? <Callout variant="danger">{error}</Callout> : null}

      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard
          title={labels.charges}
          invoice={invoice.charges.invoice}
          cash={invoice.charges.cash}
          total={invoice.charges.total}
        />
        <SummaryCard
          title={labels.paid}
          invoice={invoice.paid.invoice}
          cash={invoice.paid.cash}
          total={invoice.paid.total}
        />
        <SummaryCard
          title={labels.outstanding}
          invoice={invoice.outstanding.invoice}
          cash={invoice.outstanding.cash}
          total={invoice.outstanding.total}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="space-y-2 p-4 text-sm">
          <Row label={labels.customer} value={invoice.customer.name} />
          <Row label={labels.category} value={invoice.category?.name} />
          {invoice.shipmentFile ? (
            <div className="flex justify-between gap-3">
              <span className="text-ink-muted">{labels.shipmentFile}</span>
              <Link
                to={`/shipment-files/${invoice.shipmentFile.id}`}
                className="text-brand hover:underline"
              >
                {invoice.shipmentFile.fileNo}
              </Link>
            </div>
          ) : null}
          <Row label={labels.notes} value={invoice.notes} />
        </Card>
        <Card
          className={`p-4 ${showVatColumns ? 'md:col-span-2' : ''}`}
        >
          <h3 className="mb-3 text-sm font-semibold">{labels.linesSection}</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-muted text-left text-ink-muted">
                <tr>
                  <SortableTh sortKey="type" sort={sort} onSort={toggleSort}>
                    {t().financial.type}
                  </SortableTh>
                  {showVatColumns ? (
                    <>
                      <SortableTh
                        sortKey="netAmount"
                        sort={sort}
                        onSort={toggleSort}
                        align="right"
                      >
                        {labels.netAmount}
                      </SortableTh>
                      <th className="whitespace-nowrap px-2 py-1 text-right">
                        {labels.vatRate}
                      </th>
                      <SortableTh
                        sortKey="vatAmount"
                        sort={sort}
                        onSort={toggleSort}
                        align="right"
                      >
                        {labels.vatAmount}
                      </SortableTh>
                    </>
                  ) : null}
                  <SortableTh
                    sortKey="amount"
                    sort={sort}
                    onSort={toggleSort}
                    align="right"
                  >
                    {showVatColumns ? labels.lineTotal : t().financial.amount}
                  </SortableTh>
                  <SortableTh
                    sortKey="description"
                    sort={sort}
                    onSort={toggleSort}
                  >
                    {t().financial.description}
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {displayLines.map((line) => (
                  <tr key={line.id} className="border-t border-line">
                    <td className="whitespace-nowrap px-2 py-1">
                      {line.type === 'INVOICE'
                        ? t().financial.invoice
                        : t().financial.cash}
                    </td>
                    {showVatColumns ? (
                      <>
                        <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">
                          {formatMoney(line.netAmount ?? line.amount)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">
                          {Number(line.vatRate ?? 0)}%
                        </td>
                        <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">
                          {formatMoney(line.vatAmount ?? '0')}
                        </td>
                      </>
                    ) : null}
                    <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">
                      {formatMoney(line.amount)}
                    </td>
                    <td className="px-2 py-1">{line.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </FormPageShell>
  );
}

function SummaryCard({
  title,
  invoice,
  cash,
  total,
}: {
  title: string;
  invoice: string;
  cash: string;
  total: string;
}) {
  return (
    <Card className="p-4 text-sm">
      <div className="text-xs uppercase tracking-wide text-ink-faint">{title}</div>
      <div className="mt-2 space-y-1">
        <div className="flex justify-between">
          <span>{t().financial.invoice}</span>
          <span className="tabular-nums">{formatMoney(invoice)}</span>
        </div>
        <div className="flex justify-between">
          <span>{t().financial.cash}</span>
          <span className="tabular-nums">{formatMoney(cash)}</span>
        </div>
        <div className="flex justify-between font-semibold">
          <span>{t().financial.grandTotal}</span>
          <span className="tabular-nums">{formatMoney(total)}</span>
        </div>
      </div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-muted">{label}</span>
      <span className="text-right">{value || '—'}</span>
    </div>
  );
}
